import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { useMemo } from "react";
import { useWSQueries } from "@ws-react-query";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { QUERY_BACKGROUND_CHECKS_REQUEST } from "../../../../query/backgroundChecks/queries/useBackgroundChecksRequestQuery";
import { getBackgroundChecksRequest } from "../../../../services/backgroundChecks/getBackgroundChecksRequest";
import { usePayerBackgroundChecksConfig } from "./configs/payer/usePayerBackgroundChecksConfig";
import { usePayeeBackgroundChecksConfig } from "./configs/payee/usePayeeBackgroundChecksConfig";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";
import { getBackgroundChecksRequestFromCache } from "./configs/helpers/getBackgroundChecksRequestFromCache";
import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.BackgroundCheck &&
        requirement.dataSourceId
    )
    .map(requirement => ({
      queryKey: [QUERY_BACKGROUND_CHECKS_REQUEST, requirement.dataSourceId],
      queryFn: () => getBackgroundChecksRequest(requirement.dataSourceId),
      ...sharedRequirementsQueriesConfig
    }));

  return useWSQueries({ queries });
};

export const useBackgroundChecksRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.BackgroundCheck] => {
  const contextQueries = useContextQueries(requirements);
  const payerBackgroundChecksConfig = usePayerBackgroundChecksConfig(callback);
  const payeeBackgroundChecksConfig = usePayeeBackgroundChecksConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<
      IBackgroundCheckRequestResponse,
      RequirementType.BackgroundCheck
    >({
      payerConfig: payerBackgroundChecksConfig,
      payeeConfig: payeeBackgroundChecksConfig,
      getAdditionalData: getBackgroundChecksRequestFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getBackgroundChecksRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
